const ROW_SELECTOR = '[role="row"]';

/*
 * The trigger selector is fixed to `ClioUI::PopoverComponent`'s
 * `referenceElement` ref. Exported so consumers that take over
 * `onCellContextMenu` themselves (e.g. to layer selection behavior on top of
 * the menu-opening mechanics) can call `handleRowContextMenu` without
 * re-hardcoding this selector.
 */
export const POPOVER_TRIGGER_SELECTOR = '[x-ref="referenceElement"]';

export type ContextMenuConfig = {
  /*
   * CSS selector for the popover trigger element (relative to each row). The
   * trigger must live inside a `[x-data="popover"]` element rendered by
   * `ClioUI::PopoverComponent`.
   */
  triggerSelector: string;
};

/*
 * Resolves the popover trigger for the row the user right-clicked. AG Grid
 * renders each logical row as multiple parallel `[role="row"]` instances when
 * columns are pinned (one per viewport: left-pinned, center, right-pinned),
 * and the trigger lives in only one of them. The descendant selector matches
 * across all instances that share the row-id, so the click lands on the right
 * trigger regardless of which row instance the user clicked.
 */
function findTriggerForRow(
  target: Element,
  triggerSelector: string
): HTMLElement | null {
  const initialRow = target.closest<HTMLElement>(ROW_SELECTOR);
  if (!initialRow) return null;

  const rowId = initialRow.getAttribute("row-id");
  if (!rowId) return initialRow.querySelector<HTMLElement>(triggerSelector);

  return document.querySelector<HTMLElement>(
    `${ROW_SELECTOR}[row-id="${CSS.escape(rowId)}"] ${triggerSelector}`
  );
}

/**
 * Wires the data grid's `onCellContextMenu` to the row's popover. Mouse-
 * initiated contextmenu dispatches a bubbling `popover:open-at-coordinates`
 * event from the trigger; the popover root listens for it and opens at the
 * cursor. Keyboard contextmenu (Shift+F10 / Menu key) falls back to clicking
 * the trigger so the popover anchors to the trigger element.
 */
export function handleRowContextMenu(
  mouseEvent: MouseEvent | null,
  triggerSelector: string
): void {
  const target = mouseEvent?.target as Element | null;
  if (!target) return;

  const triggerEl = findTriggerForRow(target, triggerSelector);
  if (!triggerEl) return;

  /*
   * `button === 2` is a real right-click; `button === 0` is keyboard
   * contextmenu (Shift+F10 / Menu key). Coordinate-based detection is
   * unreliable: Firefox sets clientX/Y to the focused element's position for
   * keyboard contextmenu, and a real click at (0, 0) would be misclassified.
   */
  if (mouseEvent.button !== 2) {
    triggerEl.click();
    return;
  }

  triggerEl.dispatchEvent(
    new CustomEvent("popover:open-at-coordinates", {
      bubbles: true,
      detail: { clientX: mouseEvent.clientX, clientY: mouseEvent.clientY },
    })
  );
}
